/**
 * Sanctions list data source.
 * Fetches the OFAC SDN (Specially Designated Nationals) list.
 * Free, public domain. Updated daily.
 *
 * XML parsing runs in a Web Worker to avoid blocking the UI thread —
 * the SDN XML can be 20-30MB with 10k+ entries.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, EntityReference } from '../base-source';
import type { SanctionEntry } from '@/workers/sanctions-worker';
import { getCountryCentroid } from '@/config/country-centroids';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'sanctions';

/** Maximum SDN entries to process (balances coverage vs pipeline load). */
const MAX_ENTRIES = 2000;
/** Maximum time to wait for worker response (ms). */
const WORKER_TIMEOUT_MS = 60_000;

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

let worker: Worker | null = null;

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(
      new URL('../../workers/sanctions-worker.ts', import.meta.url),
      { type: 'module' },
    );
  }
  return worker;
}

/**
 * Send XML to the worker for parsing.
 * Returns parsed entries and publish date, or throws on error/timeout.
 */
function parseInWorker(xml: string): Promise<{ entries: SanctionEntry[]; publishDate: string | null }> {
  return new Promise((resolve, reject) => {
    const w = getWorker();

    const timer = setTimeout(() => {
      cleanup();
      // Terminate and recreate on timeout
      if (worker) {
        worker.terminate();
        worker = null;
      }
      reject(new Error('Sanctions XML parsing timed out after 60s'));
    }, WORKER_TIMEOUT_MS);

    function onMessage(e: MessageEvent): void {
      cleanup();
      if (e.data.type === 'result') {
        resolve({ entries: e.data.entries, publishDate: e.data.publishDate });
      } else if (e.data.type === 'error') {
        reject(new Error(e.data.message));
      }
    }

    function onError(e: ErrorEvent): void {
      cleanup();
      reject(new Error(e.message));
    }

    function cleanup(): void {
      clearTimeout(timer);
      w.removeEventListener('message', onMessage);
      w.removeEventListener('error', onError);
    }

    w.addEventListener('message', onMessage);
    w.addEventListener('error', onError);

    w.postMessage({ type: 'parse', xml, maxEntries: MAX_ENTRIES });
  });
}

export const sanctionsSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'Sanctions Lists',
    description: 'OFAC, EU, UN consolidated sanctions lists',
    icon: 'ban',
    category: 'government',
    updateInterval: 86_400_000, // 24 hours
    requiresApiKey: false,
    dataLicense: 'Public Domain',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] Sanctions list source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
    if (worker) {
      worker.terminate();
      worker = null;
    }
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      const url = `${getApiBase()}/api/data/sanctions`;
      const response = await fetch(url, { signal: AbortSignal.timeout(30_000) });

      if (!response.ok) {
        throw new Error(`OFAC SDN feed returned ${response.status}`);
      }

      const xml = await response.text();

      // Parse XML in Web Worker (avoids blocking UI on large XML)
      const { entries, publishDate } = await parseInWorker(xml);
      const stableTimestamp = publishDate ?? new Date().toISOString();

      // Build NormalizedEvents on main thread (country centroid lookup is cheap)
      const events: NormalizedEvent[] = [];

      for (const entry of entries) {
        const name = [entry.firstName, entry.lastName].filter(Boolean).join(' ') || 'Unknown';

        const entities: EntityReference[] = [{
          entityId: `sanctions-${entry.uid}`,
          name,
          type: entry.sdnType === 'Individual' ? 'person' : 'organization',
          confidence: 1.0,
        }];

        const event: NormalizedEvent = {
          id: `sanctions-${entry.uid}`,
          source: SOURCE_ID,
          timestamp: stableTimestamp,
          type: 'sanction',
          title: `${name} — ${entry.programs.join(', ') || 'OFAC SDN'}`,
          severity: 'medium',
          entities,
          tags: ['sanctions', 'OFAC', ...entry.programs],
          raw: {
            uid: entry.uid,
            sdnType: entry.sdnType,
            programs: entry.programs,
            country: entry.country,
          },
        };

        // Geo-locate by country
        if (entry.country) {
          const centroid = getCountryCentroid(entry.country);
          if (centroid) {
            event.location = {
              lat: centroid[0],
              lng: centroid[1],
              country: entry.country,
              name: entry.country,
            };
          }
        }

        events.push(event);
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
